import React, { forwardRef, useId } from 'react';
import clsx from 'clsx';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** When provided, rightIcon renders as a clickable button (e.g. a password show/hide toggle)
   *  instead of a decorative, non-interactive icon. */
  onRightIconClick?: () => void;
  /** aria-label for the button rendered when onRightIconClick is provided. */
  rightIconLabel?: string;
  variant?: 'default' | 'outlined';
  size?: 'sm' | 'md' | 'lg';
}

const sizeClassMap: Record<NonNullable<InputProps['size']>, string> = {
  sm: 'py-2 text-sm',
  md: 'py-3 text-base',
  lg: 'py-3.5 text-base',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({
    label,
    error,
    helperText,
    leftIcon,
    rightIcon,
    onRightIconClick,
    rightIconLabel,
    variant = 'default',
    size = 'md',
    className,
    id,
    ...props
  }, ref) => {
    const generatedId = useId();
    const inputId = id || `input-${generatedId}`;
    const helpId = helperText ? `${inputId}-help` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;

    return (
      <div className="w-full">
        {label ? (
          <label htmlFor={inputId} className="mb-2 block text-sm font-medium text-agentic-text-primary">
            {label}
          </label>
        ) : null}

        <div className="relative">
          {leftIcon ? (
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-agentic-text-muted">
              {leftIcon}
            </span>
          ) : null}

          <input
            ref={ref}
            id={inputId}
            aria-invalid={Boolean(error)}
            aria-describedby={[errorId, helpId].filter(Boolean).join(' ') || undefined}
            className={clsx(
              'w-full rounded-xl border bg-white px-4 text-agentic-text-primary placeholder:text-agentic-text-muted transition',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kv-agentic-blue focus-visible:ring-offset-2',
              sizeClassMap[size],
              variant === 'outlined' ? 'border-agentic-gray-300' : 'border-agentic-gray-300',
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              error && 'border-red-300 focus-visible:ring-red-500',
              'disabled:cursor-not-allowed disabled:bg-agentic-gray-100 disabled:text-agentic-text-muted',
              className
            )}
            {...props}
          />

          {rightIcon ? (
            onRightIconClick ? (
              <button
                type="button"
                onClick={onRightIconClick}
                aria-label={rightIconLabel}
                tabIndex={-1}
                className="absolute inset-y-0 right-3 flex items-center text-agentic-text-muted hover:text-agentic-text-primary focus:outline-none"
              >
                {rightIcon}
              </button>
            ) : (
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-agentic-text-muted">
                {rightIcon}
              </span>
            )
          ) : null}
        </div>

        {error ? (
          <p id={errorId} className="mt-1 text-sm text-red-600">
            {error}
          </p>
        ) : null}

        {helperText && !error ? (
          <p id={helpId} className="mt-1 text-sm text-agentic-text-secondary">
            {helperText}
          </p>
        ) : null}
      </div>
    );
  }
);

Input.displayName = 'Input';
